import { PublicKey } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';

import {
  getUserStateAccount,
  MAX_SOLANA_PUBKEYS_PER_CALL,
} from '@/constants/helpers';
import { globalState, program } from '@/constants/solana';
import { UserState } from '@/types/contract';

export const fetchUserState = async (user: PublicKey | string) => {
  return await program.account.userState.fetchNullable(
    getUserStateAccount(new PublicKey(user)),
  );
};

export const useFetchUserState = (user: PublicKey | string | null) => {
  return useQuery({
    queryKey: ['user-state', user],
    queryFn: async () => {
      if (!user) return null;
      return await fetchUserState(user);
    },
  });
};

export const fetchUserStates = async (
  users: PublicKey[] | string[],
): Promise<Record<string, UserState | null>> => {
  if (!globalState || users.length === 0) return {};
  const pubkeys = users.map((user) => getUserStateAccount(new PublicKey(user)));

  const result: Record<string, UserState | null> = {};
  for (let i = 0; i < pubkeys.length; i += MAX_SOLANA_PUBKEYS_PER_CALL) {
    const slice = pubkeys.slice(i, i + MAX_SOLANA_PUBKEYS_PER_CALL);
    const sliceUsers = users
      .slice(i, i + MAX_SOLANA_PUBKEYS_PER_CALL)
      .map((user) => new PublicKey(user).toBase58());
    const part = await program.account.userState.fetchMultiple(slice);

    for (let j = 0; j < sliceUsers.length; j++) {
      result[sliceUsers[j]] = part[j] ?? null;
    }
  }
  return result;
};

export const useFetchUserStates = (users: PublicKey[] | string[]) => {
  return useQuery({
    queryKey: ['user-states', users],
    enabled: users.length > 0,
    queryFn: async () => {
      return await fetchUserStates(users);
    },
  });
};
